import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Plug, ScrollText, Copy, ShieldCheck, ShieldX, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function KeysCard({ auth, onChanged }) {
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/access/keys`, auth)
      .then((r) => setKeys(r.data.keys))
      .catch((e) => console.warn("keys fetch failed:", e?.message));
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!name.trim()) return toast.error("Give the key a name first (e.g. 'Zapier', 'Mobile app')");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/access/keys`, { name: name.trim() }, auth);
      setNewKey(r.data.key);
      setName("");
      load();
      onChanged?.();
      toast.success("Key generated — copy it now, it is shown only once");
    } catch {
      toast.error("Could not generate the key");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using this key loses access immediately.`)) return;
    try {
      await axios.post(`${API}/admin/access/keys/revoke`, { id: k.id }, auth);
      load();
      onChanged?.();
      toast.success(`"${k.name}" revoked`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke the key");
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy blocked by the browser — select the key text and copy it manually");
    }
  };

  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid="access-keys-card">
      <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
        <KeyRound size={18} className="text-[#a8842c]" /> API Keys
      </h3>
      <p className="text-sm text-[#8f8571] mt-1">
        Every request to your backend must present a valid key. The <strong>Website</strong> key powers regaljewels.com
        and is protected. Generate separate keys for integrations so each can be revoked on its own.
      </p>

      <div className="flex flex-wrap gap-3 mt-5">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name — e.g. Zapier, Partner app" className="rounded-none border-[#d8cdb5] h-11 flex-1 min-w-[220px]" data-testid="access-key-name-input" />
        <Button onClick={generate} disabled={busy} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-6 transition-colors" data-testid="access-key-generate-btn">
          {busy ? "Generating..." : "Generate Key"}
        </Button>
      </div>

      {newKey && (
        <div className="border border-[#c9a24b] bg-[#faf3df] p-4 mt-4" data-testid="access-new-key-box">
          <Label className="text-[11px] uppercase tracking-wider text-[#8a6a1f]">New key — shown only once, copy it now</Label>
          <div className="flex items-center gap-2 mt-2">
            <code className="text-xs break-all flex-1 text-[#171310]" data-testid="access-new-key-value">{newKey}</code>
            <button onClick={copyKey} className="shrink-0 p-2 border border-[#c9a24b] hover:bg-[#c9a24b]/15 transition-colors" aria-label="Copy key" data-testid="access-new-key-copy">
              <Copy size={14} className="text-[#8a6a1f]" />
            </button>
          </div>
        </div>
      )}

      {!keys ? (
        <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
      ) : (
        <div className="mt-5 space-y-2" data-testid="access-keys-list">
          {keys.map((k) => (
            <div key={k.id} className="flex flex-wrap items-center justify-between gap-2 border border-[#eee6d6] px-4 py-3" data-testid={`access-key-row-${k.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="min-w-0">
                <p className="text-sm text-[#171310] font-medium flex items-center gap-2">
                  {k.name}
                  {k.protected && <span className="text-[9px] tracking-[0.15em] uppercase bg-[#171310] text-[#e9d9ac] px-1.5 py-0.5">Website</span>}
                  {k.revokedAt && <span className="text-[9px] tracking-[0.15em] uppercase bg-[#8f2f2f] text-white px-1.5 py-0.5">Revoked</span>}
                </p>
                <p className="text-[11px] text-[#8f8571] mt-0.5 font-mono">{k.masked} · created {fmtTs(k.createdAt)} · last used {fmtTs(k.lastUsedAt)}</p>
              </div>
              {!k.protected && !k.revokedAt && (
                <Button onClick={() => revoke(k)} variant="outline" className="rounded-none border-[#d8cdb5] text-[#8f2f2f] hover:border-[#8f2f2f] tracking-[0.12em] uppercase text-[10px] h-8 px-4" data-testid={`access-key-revoke-${k.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  Revoke
                </Button>
              )}
            </div>
          ))}
          {keys.length === 0 && <p className="text-sm text-[#8f8571]">No keys yet.</p>}
        </div>
      )}
    </div>
  );
}

function IntegrationsCard({ auth }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/access/integrations`, auth)
      .then((r) => setItems(r.data.integrations))
      .catch((e) => console.warn("integrations fetch failed:", e?.message));
  }, [auth]);

  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid="access-integrations-card">
      <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
        <Plug size={18} className="text-[#a8842c]" /> Connected Integrations
      </h3>
      <p className="text-sm text-[#8f8571] mt-1">Services your site is wired to right now.</p>
      {!items ? (
        <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2 mt-5">
          {items.map((it) => (
            <div key={it.name} className="flex items-start justify-between gap-2 border border-[#eee6d6] px-4 py-3">
              <div>
                <p className="text-sm text-[#171310] font-medium">{it.name}</p>
                <p className="text-[11px] text-[#8f8571] mt-0.5">{it.detail}</p>
              </div>
              {it.active
                ? <ShieldCheck size={16} className="text-[#3f6b3a] shrink-0 mt-0.5" aria-label="Active" />
                : <ShieldX size={16} className="text-[#8f2f2f] shrink-0 mt-0.5" aria-label="Not configured" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  allowed: "text-[#3f6b3a]",
  denied: "text-[#8f2f2f] font-semibold",
  bypass: "text-[#8f8571]",
};

function AccessLogCard({ auth, refreshSignal }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/access/log?limit=100&status=${filter}`, auth)
      .then((r) => setData(r.data))
      .catch((e) => console.warn("access log fetch failed:", e?.message));
  }, [auth, filter]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid="access-log-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
          <ScrollText size={18} className="text-[#a8842c]" /> Access Log
        </h3>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-[#8a6a1f] hover:text-[#171310] transition-colors" data-testid="access-log-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <p className="text-sm text-[#8f8571] mt-1">Every connection attempt — timestamp, source, endpoint and outcome. Kept 14 days.</p>

      {data && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="access-log-summary">
          <span className="text-[11px] uppercase tracking-wider border border-[#eee6d6] px-3 py-1.5 text-[#3f6b3a]">Allowed 24h: {data.last24h.allowed}</span>
          <span className="text-[11px] uppercase tracking-wider border border-[#eee6d6] px-3 py-1.5 text-[#8f2f2f]">Denied 24h: {data.last24h.denied}</span>
          <span className="text-[11px] uppercase tracking-wider border border-[#eee6d6] px-3 py-1.5 text-[#8f8571]">Exempt 24h: {data.last24h.bypass}</span>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        {[["", "All"], ["allowed", "Allowed"], ["denied", "Denied"], ["bypass", "Exempt"]].map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors ${filter === v ? "bg-[#171310] text-[#e9d9ac] border-[#171310]" : "border-[#d8cdb5] text-[#5c5240] hover:border-[#a8842c]"}`}
            data-testid={`access-log-filter-${label.toLowerCase()}`}>
            {label}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-left text-xs" data-testid="access-log-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#7a7160] border-b border-[#eee6d6]">
                <th className="py-2 pr-3">Time</th>
                <th className="py-2 pr-3">Source</th>
                <th className="py-2 pr-3">Request</th>
                <th className="py-2 pr-3">Key</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={i} className="border-b border-[#f4efe3]">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-[#5c5240]">{fmtTs(e.ts)}</td>
                  <td className="py-1.5 pr-3 font-mono text-[#5c5240]">{e.ip}</td>
                  <td className="py-1.5 pr-3 font-mono text-[#171310] max-w-[260px] truncate">{e.method} {e.path}</td>
                  <td className="py-1.5 pr-3 text-[#5c5240]">{e.key}</td>
                  <td className={`py-1.5 uppercase text-[10px] tracking-wider ${STATUS_STYLE[e.status] || ""}`}>{e.status === "bypass" ? "exempt" : e.status}</td>
                </tr>
              ))}
              {data.entries.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-[#8f8571]">No entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AccessTab({ auth }) {
  const [refreshSignal, setRefreshSignal] = useState(0);
  return (
    <div className="space-y-8 max-w-4xl" data-testid="access-tab">
      <KeysCard auth={auth} onChanged={() => setRefreshSignal((n) => n + 1)} />
      <IntegrationsCard auth={auth} />
      <AccessLogCard auth={auth} refreshSignal={refreshSignal} />
    </div>
  );
}
